import api from "@/lib/api";

// Deployment proxies cap single request bodies (~1 MB) — large product
// images/brochures failed with "Failed to fetch" in production. Files
// bigger than CHUNK_SIZE are sliced into small base64 JSON chunks that
// the backend reassembles.
const CHUNK_SIZE = 700 * 1024;

const blobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1] || "");
    r.onerror = reject;
    r.readAsDataURL(blob);
  });

const randomId = () => {
  const a = new Uint8Array(16);
  crypto.getRandomValues(a);
  return [...a].map((b) => b.toString(16).padStart(2, "0")).join("");
};

// Uploads a file to the backend media store; returns {id, url, filename, ...}.
// `url` is a same-origin path ("/api/media/<id>") usable directly in <img src>.
// onProgress (optional): called with 0..100.
export async function uploadMedia(file, onProgress) {
  if (file.size > 10 * 1024 * 1024) {
    throw new Error("File too large (max 10 MB)");
  }
  if (file.size <= CHUNK_SIZE) {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/media/upload", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    onProgress?.(100);
    return data;
  }
  const uploadId = randomId();
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
  let result = null;
  for (let i = 0; i < totalChunks; i++) {
    const slice = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
    const data64 = await blobToBase64(slice);
    const { data } = await api.post("/media/upload-chunk", {
      upload_id: uploadId,
      chunk_index: i,
      total_chunks: totalChunks,
      filename: file.name || "file",
      content_type: file.type || "application/octet-stream",
      data: data64,
    });
    onProgress?.(Math.round(((i + 1) / totalChunks) * 100));
    if (data.complete) result = data;
  }
  if (!result) throw new Error("Upload did not complete — please retry");
  return result;
}
